/**
 * OdysseyTales's word key grammar (ADR-119 decision 2): the game's derived namespaces, handed to
 * `daedalus-core/words`'s `keyGrammar`. Each namespace is a kind of the game's records whose words are keyed by their
 * ids; `worlds` and `content` are local, since the page looks their keys up by an id it already holds.
 */

import { keyGrammar } from "daedalus-core/words";

/**
 * The derived keys' namespaces, closed: the records whose words are keyed by their ids.
 */
export const DERIVED_NAMESPACES = Object.freeze(["items", "abilities", "effects", "looks", "creatures", "emote", "emoji", "worlds", "content"] as const);

/**
 * One of the derived keys' namespaces.
 */
export type DerivedNamespace = (typeof DERIVED_NAMESPACES)[number];

/**
 * The namespaces whose keys never travel, since the page looks them up by an id it already holds.
 */
export const LOCAL_NAMESPACES: readonly DerivedNamespace[] = Object.freeze(["worlds", "content"] as const);

/**
 * The game's key grammar, built once from the two lists.
 */
const GRAMMAR = keyGrammar({ derived: DERIVED_NAMESPACES, local: LOCAL_NAMESPACES });

/**
 * A derived key of the game: one of `DERIVED_NAMESPACES`, one or two id segments of `[a-z0-9_-]` and a `camelCase`
 * field.
 */
export const DERIVED_KEY_PATTERN: RegExp = GRAMMAR.derivedPattern;

/**
 * Whether a value is a catalogue key of the game: the catalogue pattern, at most 64 characters, and a file that is
 * not one of `DERIVED_NAMESPACES`.
 */
export const isCatalogueKey: (value: unknown) => value is string = GRAMMAR.isCatalogueKey;

/**
 * Whether a value is a derived key of the game: `DERIVED_KEY_PATTERN`, at most 160 characters under `worlds` or
 * `content` and 96 under any other namespace.
 */
export const isDerivedKey: (value: unknown) => value is string = GRAMMAR.isDerivedKey;
